import React, { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { showErrorToast } from "~/components/Toast";
import { fetchConversation } from "~/store/actions/actions.conversations";
import { makeObjectPresenceChannelNameSelector } from "~/store/selectors/selector.realTime";
import { makeGetUserConversationSelector } from "~/store/selectors/selector.userConversations";
import { CREATE_TASK_MODE } from "./index.jsx";

export const useConversationContent = (id) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isLeavingConversation = useRef(false);

  const [screen, setScreen] = useState("conversation");
  const [mode, setMode] = useState("");
  const [selectedMessageIds, setSelectedMessageIds] = useState([]);

  const getUserConversation = useMemo(() => makeGetUserConversationSelector(id), [id]);
  const userConversation = useSelector(getUserConversation);
  const defaultNewMessagesSince = useRef();

  defaultNewMessagesSince.current = userConversation?.new_messages_since;

  const [newMessagesSince, setNewMessagesSince] = useState(() => defaultNewMessagesSince.current);

  const resetNewMessageSince = useCallback(() => {
    setNewMessagesSince(defaultNewMessagesSince.current);
  }, []);

  const getObjectPresenceChannelName = useMemo(() => makeObjectPresenceChannelNameSelector("conversation"), []);

  const channelName = useSelector((state) => getObjectPresenceChannelName(state, id));

  const onChangeSelectedMessage = useCallback((id, checked) => {
    setSelectedMessageIds((prevState) => {
      if (checked) {
        return [...new Set([...prevState, id])];
      }
      return prevState.filter((value) => value !== id);
    });
  }, []);

  const mapOfCheckedMessages = useMemo(() => {
    return selectedMessageIds.reduce((acc, value) => {
      return { ...acc, [value]: true };
    }, {});
  }, [selectedMessageIds]);

  const setIsLeavingConversation = useCallback((value) => {
    isLeavingConversation.current = value;
  }, []);

  const manualSetNewMessageSince = useCallback(
    (value) => {
      isLeavingConversation.current = true;
      setNewMessagesSince(value);
    },
    [setNewMessagesSince]
  );

  React.useEffect(() => {
    dispatch(fetchConversation(id)).then(({ error }) => {
      if (error) {
        showErrorToast(t("Failed to open conversation"));
      }
    });

    return () => {
      !isLeavingConversation.current && dispatch(fetchConversation(id));
    };
  }, [id, dispatch, t]);

  React.useEffect(() => {
    if (mode !== CREATE_TASK_MODE) {
      setSelectedMessageIds([]);
    }
  }, [mode]);

  return {
    mode,
    setMode,
    screen,
    setScreen,
    selectedMessageIds,
    newMessagesSince,
    setNewMessagesSince,
    channelName,
    onChangeSelectedMessage,
    mapOfCheckedMessages,
    resetNewMessageSince,
    setIsLeavingConversation,
    manualSetNewMessageSince,
  };
};
